/**
 * Activity WebSocket Client
 * 실시간 활동 피드를 위한 WebSocket 클라이언트
 */

class ActivityWebSocketClient {
  constructor() {
    this.ws = null;
    this.listeners = new Map();
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectDelay = 3000;
    this.isConnecting = false;
    this.token = null;
  }

  /**
   * WebSocket 연결
   */
  connect(token) {
    if (this.isConnecting || (this.ws && this.ws.readyState === WebSocket.OPEN)) {
      return;
    }

    this.token = token;
    this.isConnecting = true;

    // WebSocket URL 결정
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = process.env.REACT_APP_AUTH_WS_URL ||
      (window.location.hostname === 'localhost'
        ? 'localhost:4001'
        : 'auth.ilouli.com');

    const wsUrl = `${protocol}//${host}/ws/activity?token=${token}`;

    try {
      this.ws = new WebSocket(wsUrl);

      this.ws.onopen = () => {
        console.log('[WS] Connected to activity feed');
        this.isConnecting = false;
        this.reconnectAttempts = 0;
        this.emit('connected', { timestamp: Date.now() });
      };

      this.ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          this.handleMessage(message);
        } catch (err) {
          console.error('[WS] Message parse error:', err);
        }
      };

      this.ws.onclose = (event) => {
        console.log('[WS] Disconnected:', event.code, event.reason);
        this.isConnecting = false;
        this.emit('disconnected', { code: event.code, reason: event.reason });

        // 자동 재연결 시도
        if (this.reconnectAttempts < this.maxReconnectAttempts && this.token) {
          this.reconnectAttempts++;
          console.log(`[WS] Reconnecting... (${this.reconnectAttempts}/${this.maxReconnectAttempts})`);
          setTimeout(() => this.connect(this.token), this.reconnectDelay);
        }
      };

      this.ws.onerror = (error) => {
        console.error('[WS] Error:', error);
        this.isConnecting = false;
        this.emit('error', { error });
      };

    } catch (err) {
      console.error('[WS] Connection error:', err);
      this.isConnecting = false;
    }
  }

  /**
   * 연결 종료
   */
  disconnect() {
    this.token = null;
    this.reconnectAttempts = this.maxReconnectAttempts; // 재연결 방지
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
  }

  /**
   * 메시지 처리
   */
  handleMessage(message) {
    const { event, data } = message;

    switch (event) {
      case 'connected':
        console.log('[WS] Server confirmed connection');
        break;

      case 'activity:new':
        this.emit('activity', data);
        break;

      case 'alert:anomaly':
        this.emit('anomaly', data);
        break;

      case 'subscribed':
        console.log('[WS] Subscription confirmed:', data.filters);
        break;

      case 'pong':
        // Heartbeat response
        break;

      default:
        console.log('[WS] Unknown event:', event, data);
    }
  }

  /**
   * 필터 구독
   */
  subscribe(filters = {}) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({
        event: 'subscribe',
        filters
      }));
    }
  }

  /**
   * 구독 해제
   */
  unsubscribe() {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ event: 'unsubscribe' }));
    }
  }

  /**
   * Ping 전송
   */
  ping() {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ event: 'ping' }));
    }
  }

  /**
   * 이벤트 리스너 등록
   */
  on(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, []);
    }
    this.listeners.get(event).push(callback);

    // cleanup 함수 반환
    return () => this.off(event, callback);
  }

  /**
   * 이벤트 리스너 해제
   */
  off(event, callback) {
    if (this.listeners.has(event)) {
      const callbacks = this.listeners.get(event);
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
      }
    }
  }

  /**
   * 이벤트 발생
   */
  emit(event, data) {
    if (this.listeners.has(event)) {
      this.listeners.get(event).forEach(callback => {
        try {
          callback(data);
        } catch (err) {
          console.error('[WS] Listener error:', err);
        }
      });
    }
  }

  /**
   * 연결 상태 확인
   */
  isConnected() {
    return this.ws && this.ws.readyState === WebSocket.OPEN;
  }
}

// 싱글톤 인스턴스
const activityWebSocket = new ActivityWebSocketClient();

export default activityWebSocket;
